import React from "react";
import { Img, staticFile } from "remotion";
import { FLOAT_SHADOW, LOOK } from "../look";

/**
 * Screen aspect of every app capture we have (1855×4036 inside the
 * framed wallet PNGs, 786×1704 for the raw captures, 1856 wide for the
 * Explorer long-shot). Height = width × SCREEN_RATIO.
 */
export const SCREEN_RATIO = 4036 / 1855;

type Props = {
  /** Outer width of the device in px. */
  width: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
};

/**
 * Minimal iPhone-style device: dark titanium bezel, rounded screen,
 * dynamic island. The screen is a positioned, clipped box, so children
 * are absolutely laid out in screen pixels (0..screenWidth).
 */
export const Phone: React.FC<Props> = ({ width, children, style }) => {
  const bezel = width * 0.035;
  const screenW = width - bezel * 2;
  const screenH = screenW * SCREEN_RATIO;
  const outerRadius = width * 0.17;
  return (
    <div
      style={{
        width,
        height: screenH + bezel * 2,
        padding: bezel,
        borderRadius: outerRadius,
        background: "linear-gradient(145deg, #3a3d44 0%, #121316 40%, #26282d 100%)",
        boxShadow: `${FLOAT_SHADOW}, inset 0 0 0 ${Math.max(1, width * 0.004)}px rgba(255,255,255,0.18)`,
        position: "relative",
        ...style,
      }}
    >
      <div
        style={{
          position: "relative",
          width: screenW,
          height: screenH,
          borderRadius: outerRadius - bezel,
          overflow: "hidden",
          background: LOOK.white,
        }}
      >
        {children}
        {/* Dynamic island, always on top of the screen content. */}
        <div
          style={{
            position: "absolute",
            top: screenW * 0.03,
            left: "50%",
            width: screenW * 0.3,
            height: screenW * 0.085,
            marginLeft: -(screenW * 0.15),
            borderRadius: screenW,
            background: "#000",
          }}
        />
      </div>
    </div>
  );
};

/** Screen width for a given device width (mirrors the bezel maths above). */
export const screenWidthOf = (phoneWidth: number) => phoneWidth * (1 - 0.07);

type ShotProps = {
  /** Screen width in px — use `screenWidthOf(phoneWidth)`. */
  screenWidth: number;
  style?: React.CSSProperties;
};

/**
 * Full-bleed raw capture (786×1704 family). `scrollY` is in screen px.
 */
export const RawShot: React.FC<
  ShotProps & { src: string; scrollY?: number; naturalWidth?: number }
> = ({ src, screenWidth, scrollY = 0, style }) => (
  <Img
    src={staticFile(src)}
    style={{
      position: "absolute",
      left: 0,
      top: -scrollY,
      width: screenWidth,
      display: "block",
      ...style,
    }}
  />
);
